/**
 * SourceMiniChart Component
 *
 * A small sparkline chart showing volume trends over time.
 * Used in the expanded source details view.
 */

import { ResponsiveContainer, AreaChart, Area, Tooltip, XAxis } from "recharts";
import type { SourceDailyStat } from "@/lib/api";

interface SourceMiniChartProps {
  data: SourceDailyStat[];
  height?: number;
  showTooltip?: boolean;
}

export function SourceMiniChart({
  data,
  height = 60,
  showTooltip = true,
}: SourceMiniChartProps) {
  // Transform data for the chart
  const chartData = data.map((d) => ({
    date: d.date,
    volume: d.message_count,
    // Format date for tooltip
    displayDate: new Date(d.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
  }));

  // If no data, show placeholder
  if (chartData.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-slate-400 text-xs"
        style={{ height }}
      >
        No volume data
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart
        data={chartData}
        margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
      >
        <defs>
          <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
            <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.05} />
          </linearGradient>
        </defs>
        {showTooltip && (
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return (
                <div className="bg-slate-800 text-white text-xs px-2 py-1 rounded shadow-lg">
                  <div className="font-medium">{d.displayDate}</div>
                  <div className="text-amber-300">
                    {d.volume.toLocaleString()} messages
                  </div>
                </div>
              );
            }}
          />
        )}
        <XAxis dataKey="date" hide />
        <Area
          type="monotone"
          dataKey="volume"
          stroke="#f59e0b"
          strokeWidth={1.5}
          fill="url(#volumeGradient)"
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
